
import React, { useState, useMemo } from 'react';
import { Booklet, AppSettings } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Clock, FileText, BookOpen, LogIn, Instagram, Facebook, Linkedin, Phone, Mail, X, Search, LayoutGrid, List as ListIcon, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DescriptionModal from './DescriptionModal';

interface PublicLibraryProps {
  booklets: Booklet[];
  isLoading?: boolean;
  appSettings: AppSettings;
  onView: (id: string) => void;
}

// Clamped description preview; "Read more" opens a themed modal with the full
// title, description and cover (instead of expanding inline).
const ExpandableText: React.FC<{ text?: string; className?: string; title?: string; thumbnail?: string }> = ({ text, className = '', title, thumbnail }) => {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    const isLong = text.length > 140;
    return (
        <div>
            <p className={`${className} line-clamp-2`}>{text}</p>
            {isLong && (
                <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                    className="pointer-events-auto text-[11px] font-bold uppercase tracking-wide text-dark/60 hover:text-dark mt-1 underline underline-offset-2 transition-colors"
                >
                    Read more
                </button>
            )}
            <DescriptionModal isOpen={open} onClose={() => setOpen(false)} title={title || ''} description={text} thumbnail={thumbnail} />
        </div>
    );
};

const PinterestIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="currentColor" 
        className={className} 
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.644 0-5.784 2.739-5.784 5.562 0 1.102.425 2.284.954 2.927.105.128.121.24.09.372-.096.406-.314 1.291-.357 1.475-.043.178-.181.214-.416.128-1.545-.719-2.511-2.684-2.511-4.328 0-3.525 2.562-6.75 7.394-6.75 3.885 0 6.903 2.812 6.903 6.57 0 3.921-2.471 7.082-5.901 7.082-1.156 0-2.238-.602-2.607-1.305 0 0-.617 2.344-.766 2.915-.275 1.055-1.008 2.368-1.5 3.181 1.177.361 2.417.558 3.706.558 6.62 0 11.987-5.365 11.987-11.987C24.005 5.367 18.638 0 12.017 0z"/>
    </svg>
);

const PublicLibrary: React.FC<PublicLibraryProps> = ({ booklets, isLoading = false, appSettings, onView }) => {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter logic
  const filteredBooklets = useMemo(() => {
    if (!searchQuery.trim()) return booklets;
    const lowerQuery = searchQuery.toLowerCase();
    return booklets.filter(b => 
      b.title.toLowerCase().includes(lowerQuery) || 
      b.description?.toLowerCase().includes(lowerQuery)
    );
  }, [booklets, searchQuery]);

  // Determine if social section should be shown
  const showSocials = appSettings.showSocials !== false; 
  // Filter visible brands
  const visibleBrands = appSettings.footerBrands?.filter(b => b.visible !== false && b.url) || [];

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex flex-col font-sans">
      {/* Public Notice Banner */}
      {appSettings.publicNoticeBanner && (
        <div className="bg-dark text-white text-center py-2 px-4 text-sm font-medium tracking-wide">
          {appSettings.publicNoticeBanner}
        </div>
      )}
      
      {/* Public Header */}
      <header className="bg-white border-b border-warm/10 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             {appSettings.logoUrl ? (
                <img src={appSettings.logoUrl} alt="Logo" className="max-h-12 w-auto object-contain" />
             ) : (
                <>
                    <BookOpen className="h-8 w-8 text-dark" strokeWidth={1} />
                    <span className="font-serif text-xl font-bold text-dark hidden sm:block">
                        {appSettings.companyName || "Esselle Retail"}
                    </span>
                </>
             )}
          </div>
          
          <Link to="/login" className="flex items-center gap-2 text-sm font-semibold text-cool hover:text-dark transition-colors px-4 py-2 rounded-full hover:bg-gray-50 border border-transparent hover:border-warm/10">
            <LogIn size={16} />
            <span>Admin Login</span>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-white pb-12 pt-16 px-6 text-center border-b border-warm/10">
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
        >
            <h1 className="font-serif text-4xl md:text-5xl text-dark mb-4">Publication Library</h1>
            <p className="text-cool text-lg leading-relaxed mb-8">
                Browse our collection of digital catalogs, lookbooks, and reports.
            </p>
            
            {/* Search Bar in Hero */}
            <div className="max-w-md mx-auto relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-cool/50 group-focus-within:text-dark transition-colors" size={20} />
                <input 
                    type="text" 
                    placeholder="Search publications..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-6 py-4 bg-gray-50 border border-warm/20 rounded-full text-base focus:outline-none focus:border-dark/30 focus:bg-white focus:shadow-lg transition-all"
                />
            </div>
        </motion.div>
      </div>

      {/* Toolbar (View Toggle) */}
      <div className="max-w-7xl mx-auto px-6 pt-8 w-full flex justify-between items-center">
         <div className="text-xs font-bold uppercase tracking-widest text-cool/50">
             {isLoading && booklets.length === 0 ? 'Loading\u2026' : `${filteredBooklets.length} Results`}
         </div>
         <div className="flex items-center bg-white border border-warm/20 rounded-full p-1 shadow-sm">
            <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-full transition-all ${viewMode === 'grid' ? 'bg-dark text-white shadow-md' : 'text-cool hover:bg-gray-50'}`}
                title="Grid View"
            >
                <LayoutGrid size={16} />
            </button>
            <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-full transition-all ${viewMode === 'list' ? 'bg-dark text-white shadow-md' : 'text-cool hover:bg-gray-50'}`}
                title="List View"
            >
                <ListIcon size={16} />
            </button>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="flex-1 max-w-7xl mx-auto px-6 py-8 w-full">
        {isLoading && booklets.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12 items-start">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                        <div className="bg-warm/10 rounded-lg aspect-[3/4] mb-4 border border-warm/10" />
                        <div className="h-3 bg-warm/10 rounded w-1/3 mb-2" />
                        <div className="h-4 bg-warm/20 rounded w-3/4" />
                    </div>
                ))}
            </div>
        ) : filteredBooklets.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-warm/10">
                <FileText className="mx-auto text-warm/30 mb-4" size={48} strokeWidth={1} />
                <p className="text-cool">No publications found matching your criteria.</p>
                {searchQuery && (
                    <button 
                        onClick={() => setSearchQuery('')}
                        className="mt-4 text-sm font-bold text-dark hover:underline"
                    >
                        Clear Search
                    </button>
                )}
            </div>
        ) : (
            <>
            {viewMode === 'grid' ? (
                // --- GRID VIEW ---
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-16 items-start">
                    {filteredBooklets.map((item, index) => {
                        const displayDate = item.scheduledAt || item.createdAt || Date.now();

                        return (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05, duration: 0.5 }}
                            className="group relative"
                        >
                        {/* Card Cover */}
                        <div 
                            className="relative w-full mb-6 shadow-md transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-dark/20 group-hover:-translate-y-2 cursor-pointer bg-white rounded-sm overflow-hidden aspect-[3/4]"
                            onClick={() => onView(item.id)}
                        >
                            <div className="absolute inset-0 bg-dark/0 group-hover:bg-dark/5 transition-colors z-10" />
                            
                            {item.coverUrl ? (
                            <img 
                                src={item.coverUrl} 
                                alt={item.title} 
                                className="w-full h-full object-cover"
                            />
                            ) : (
                            <div className="w-full h-full bg-warm/20 flex items-center justify-center">
                                <span className="font-serif text-xl text-cool/40 uppercase tracking-tighter">Edition</span>
                            </div>
                            )}
                            
                            {/* Hover Action Overlay */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 gap-3">
                            <div className="bg-white/95 backdrop-blur-sm text-dark px-6 py-3 rounded-full font-bold shadow-xl flex items-center gap-2 transform scale-90 group-hover:scale-100 transition-transform">
                                <Eye size={18} />
                                Read Now
                            </div>
                            </div>
                        </div>

                        {/* Meta Data */}
                        <div className="space-y-2 px-1 pointer-events-none">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-warm uppercase tracking-[0.2em]">
                            <Clock size={10} />
                            {new Date(displayDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                            </div>
                            <h3 className="font-serif text-lg text-dark leading-tight mb-2">
                            {item.title}
                            </h3>
                            <ExpandableText text={item.description} className="text-xs text-cool/60 leading-relaxed" title={item.title} thumbnail={item.coverUrl || item.thumbnail} />
                        </div>
                        </motion.div>
                        );
                    })}
                </div>
            ) : (
                 // --- LIST VIEW ---
                 <div className="flex flex-col gap-4">
                     {filteredBooklets.map((item, index) => {
                         const displayDate = item.scheduledAt || item.createdAt || Date.now();
                         return (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.03, duration: 0.3 }}
                                className="group bg-white rounded-xl border border-warm/10 p-4 md:p-6 flex items-center gap-6 hover:shadow-lg hover:border-warm/30 transition-all cursor-pointer"
                                onClick={() => onView(item.id)}
                            >
                                {/* Thumbnail */}
                                <div className="w-20 md:w-24 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0 relative shadow-sm group-hover:shadow-md transition-shadow self-start aspect-[3/4]">
                                     {item.coverUrl ? (
                                         <img src={item.coverUrl} className="w-full h-full object-cover" alt="" />
                                     ) : (
                                         <div className="w-full h-full flex items-center justify-center bg-warm/10">
                                            <FileText size={24} className="text-warm/40"/>
                                         </div>
                                     )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0 flex flex-col h-full justify-center">
                                    <div className="flex items-center gap-2 mb-2 text-[10px] font-bold text-warm uppercase tracking-widest">
                                        <Clock size={12} />
                                        <span>{new Date(displayDate).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                                    </div>
                                    <h3 className="font-serif text-xl md:text-2xl text-dark mb-2 truncate group-hover:text-warm transition-colors">{item.title}</h3>
                                    <ExpandableText text={item.description} className="text-sm text-cool/70 leading-relaxed" title={item.title} thumbnail={item.coverUrl || item.thumbnail} />
                                </div>

                                {/* Action Icon */}
                                <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 group-hover:bg-dark group-hover:text-white transition-colors">
                                    <ChevronRight size={20} />
                                </div>
                            </motion.div>
                         );
                     })}
                 </div>
            )}
            </>
        )}
      </div>

      {/* Modern Footer */}
      <footer className="bg-white border-t border-warm/10 pt-20 pb-10 mt-auto">
        <div className="max-w-7xl mx-auto px-6">
            
            <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-12">
                
                {/* COLUMN 1: Socials (Span 3) */}
                <div className="md:col-span-3 flex flex-col items-center md:items-start space-y-6 order-3 md:order-1">
                    {showSocials && (
                        <>
                            <h4 className="font-serif text-xs font-bold text-dark uppercase tracking-widest border-b border-warm/20 pb-2 w-12 text-center md:text-left">Social</h4>
                            <div className="flex gap-4">
                                {appSettings.socialLinks?.instagram && (
                                    <a href={appSettings.socialLinks.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-warm/20 hover:border-dark hover:bg-dark hover:text-white flex items-center justify-center text-cool transition-all duration-300">
                                        <Instagram size={18} />
                                    </a>
                                )}
                                {appSettings.socialLinks?.facebook && (
                                    <a href={appSettings.socialLinks.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-warm/20 hover:border-dark hover:bg-dark hover:text-white flex items-center justify-center text-cool transition-all duration-300">
                                        <Facebook size={18} />
                                    </a>
                                )}
                                {appSettings.socialLinks?.linkedin && (
                                    <a href={appSettings.socialLinks.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-warm/20 hover:border-dark hover:bg-dark hover:text-white flex items-center justify-center text-cool transition-all duration-300">
                                        <Linkedin size={18} />
                                    </a>
                                )}
                                {appSettings.socialLinks?.pinterest && (
                                     <a href={appSettings.socialLinks.pinterest} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-warm/20 hover:border-dark hover:bg-dark hover:text-white flex items-center justify-center text-cool transition-all duration-300">
                                        <PinterestIcon size={18} />
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* COLUMN 2: Identity & Contact (Span 6) - Centered with borders */}
                <div className="md:col-span-6 flex flex-col items-center text-center order-1 md:order-2 md:border-x border-warm/10 md:px-12 justify-center">
                    
                    <div className="space-y-6">
                        <h3 className="font-serif text-2xl text-dark">Contact Us</h3>
                        <div className="text-sm text-cool leading-loose">
                            <p>The Old Town Hall, 8 Bacup Rd,<br/>Rossendale BB4 7LY</p>
                            <div className="flex flex-col items-center gap-2 mt-4">
                                <span className="font-semibold text-dark flex items-center gap-2">
                                    <Phone size={14} className="text-warm" /> 0333 016 4134
                                </span>
                                <a href="mailto:customerservices@esselleretail.com" className="text-cool hover:text-dark transition-colors border-b border-transparent hover:border-dark pb-0.5 flex items-center gap-2">
                                    <Mail size={14} className="text-warm" /> customerservices@esselleretail.com
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COLUMN 3: Brands (Span 3) */}
                <div className="md:col-span-3 flex flex-col items-center md:items-end space-y-6 order-2 md:order-3">
                     {visibleBrands.length > 0 && (
                        <>
                            <h4 className="font-serif text-xs font-bold text-dark uppercase tracking-widest border-b border-warm/20 pb-2 w-16 text-center md:text-right">Brands</h4>
                            
                            <div className="w-full">
                                {/* Flex wrap to handle variable number of brands gracefully */}
                                <div className="flex flex-wrap justify-center md:justify-end gap-4">
                                    {visibleBrands.map((brand, idx) => (
                                        <a key={idx} href={brand.url} target="_blank" rel="noreferrer" className="group flex items-center justify-center h-12 w-24">
                                            {brand.logoUrl ? (
                                                <img 
                                                    src={brand.logoUrl} 
                                                    alt={brand.name} 
                                                    className="max-h-12 max-w-[96px] w-auto h-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" 
                                                />
                                            ) : (
                                                <span className="text-[10px] font-semibold text-cool hover:text-dark border border-warm/20 px-2 py-1 rounded">{brand.name}</span>
                                            )}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </>
                     )}
                </div>
            </div>
            
            <div className="mt-20 pt-8 border-t border-warm/10 flex flex-col md:flex-row items-center justify-between text-[10px] text-cool/40 uppercase tracking-widest gap-4">
                <p>&copy; {new Date().getFullYear()} {appSettings.companyName || "Esselle Retail Limited"}. All rights reserved.</p>
                <div className="flex gap-6">
                    <button onClick={() => setActiveModal('privacy')} className="hover:text-dark transition-colors">Privacy Policy</button>
                    <button onClick={() => setActiveModal('terms')} className="hover:text-dark transition-colors">Terms of Service</button>
                </div>
            </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence>
        {activeModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    exit={{ opacity: 0 }} 
                    onClick={() => setActiveModal(null)} 
                    className="absolute inset-0 bg-dark/60 backdrop-blur-sm" 
                />
                
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                    animate={{ scale: 1, opacity: 1, y: 0 }} 
                    exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                    className="relative bg-white w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden"
                >
                    <div className="flex justify-between items-center px-6 py-4 border-b border-warm/10 bg-gray-50 flex-shrink-0">
                        <h2 className="font-serif text-xl text-dark">
                            {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                        </h2>
                        <button 
                            onClick={() => setActiveModal(null)}
                            className="text-cool hover:text-dark p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="p-8 overflow-y-auto text-sm text-cool leading-relaxed whitespace-pre-wrap font-serif">
                        {activeModal === 'privacy' 
                            ? (appSettings.privacyPolicy || "No privacy policy defined.") 
                            : (appSettings.termsOfService || "No terms of service defined.")}
                    </div>
                </motion.div>
            </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PublicLibrary;
